import React, { useMemo, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Smartphone, Monitor, RefreshCw, Mail } from 'lucide-react'
import InstagramPreview from './InstagramPreview'
import type { SocialPost } from '@/features/social/types'

interface SocialPreviewProps {
  platform: string
  postData: Partial<SocialPost>
  accountName?: string
  accountAvatar?: string
}

interface PlatformPreviewProps {
  accountName?: string
  accountAvatar?: string
  text: string
  hashtags: string[]
  primaryImage?: string
  secondaryImages?: string[]
  deviceMode: 'mobile' | 'desktop'
}

const MAX_TWEET_LENGTH = 280

export default function SocialPreview({
  platform,
  postData,
  accountName,
  accountAvatar
}: SocialPreviewProps) {
  const [deviceMode, setDeviceMode] = useState<'mobile' | 'desktop'>('mobile')
  const [activeTab, setActiveTab] = useState(platform.toLowerCase())

  const primaryImage = postData.image_urls?.[0]
  const secondaryImages = postData.image_urls?.slice(1, 4) ?? []
  const hashtags = useMemo(() => postData.hashtags ?? [], [postData.hashtags])

  const combinedText = useMemo(() => {
    const base = postData.caption || postData.content || ''
    const hashtagString = hashtags.join(' ')
    return [base.trim(), hashtagString.trim()].filter(Boolean).join('\n\n')
  }, [postData.caption, postData.content, hashtags])

  // Sync active tab with platform prop if it changes
  React.useEffect(() => {
    if (platform) {
      setActiveTab(platform.toLowerCase())
    }
  }, [platform])

  return (
    <Card className="sticky top-24 border-border/40 shadow-sm overflow-hidden">
      <CardHeader className="pb-2 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Preview
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={deviceMode === 'mobile' ? 'bg-background shadow-sm' : 'text-muted-foreground'}
              onClick={() => setDeviceMode('mobile')}
            >
              <Smartphone className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={deviceMode === 'desktop' ? 'bg-background shadow-sm' : 'text-muted-foreground'}
              onClick={() => setDeviceMode('desktop')}
            >
              <Monitor className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 bg-muted/10 min-h-[600px] relative">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-4 pt-4">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="instagram">Instagram</TabsTrigger>
              <TabsTrigger value="facebook">Facebook</TabsTrigger>
              <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
              <TabsTrigger value="twitter">X (Twitter)</TabsTrigger>
            </TabsList>
          </div>

          <div className="p-4 flex justify-center">
            <div className={deviceMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]'}>
              <TabsContent value="instagram" className="mt-0">
                <InstagramPreview
                  caption={postData.caption || ''}
                  images={postData.image_urls || []}
                  hashtags={hashtags}
                  accountName={accountName}
                  accountAvatar={accountAvatar}
                />
              </TabsContent>

              <TabsContent value="facebook" className="mt-0">
                <FacebookPreview
                  accountName={accountName}
                  accountAvatar={accountAvatar}
                  text={combinedText}
                  hashtags={hashtags}
                  primaryImage={primaryImage}
                  secondaryImages={secondaryImages}
                  deviceMode={deviceMode}
                />
              </TabsContent>

                <TabsContent value="linkedin" className="mt-0">
                  <LinkedInPreview
                    accountName={accountName}
                    accountAvatar={accountAvatar}
                    text={combinedText}
                    hashtags={hashtags}
                    primaryImage={primaryImage}
                    deviceMode={deviceMode}
                  />
                </TabsContent>

              <TabsContent value="twitter" className="mt-0">
                <TwitterPreview
                  accountName={accountName}
                  accountAvatar={accountAvatar}
                  text={combinedText}
                  hashtags={hashtags}
                  primaryImage={primaryImage}
                  deviceMode={deviceMode}
                />
              </TabsContent>
            </div>
          </div>
        </Tabs>

        {/* Actions Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur border-t flex justify-between items-center">
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3 mr-2" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
            <Mail className="h-3 w-3 mr-2" />
            Send Test
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function FacebookPreview({
  accountName = 'Fix My Brick',
  accountAvatar,
  text,
  hashtags,
  primaryImage,
  secondaryImages,
  deviceMode
}: PlatformPreviewProps) {
  return (
    <div className="rounded-2xl border bg-background shadow-sm">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar className="h-12 w-12">
          {accountAvatar ? <AvatarImage src={accountAvatar} alt={accountName} /> : <AvatarFallback>{initialsFor(accountName)}</AvatarFallback>}
        </Avatar>
        <div>
          <p className="text-sm font-semibold">{accountName}</p>
          <p className="text-xs text-muted-foreground">Sponsored · {deviceMode === 'mobile' ? 'Just now' : '1m'}</p>
        </div>
      </div>
      <div className="px-4 pb-4 text-sm whitespace-pre-line text-foreground">
        {text || 'Add caption to see preview text'}
        {hashtags.length > 0 && (
          <p className="mt-2 text-primary/80">{hashtags.join(' ')}</p>
        )}
      </div>
      {primaryImage && (
        <div className="border-t">
          <img src={primaryImage} alt="Primary media" className="h-64 w-full object-cover" />
        </div>
      )}
      {secondaryImages && secondaryImages.length > 0 && (
        <div className="grid grid-cols-3 gap-1 p-2 bg-black/5">
          {secondaryImages.map((img, idx) => (
            <img key={`${img}-${idx}`} src={img} alt={`Media ${idx}`} className="h-20 w-full object-cover rounded-md" />
          ))}
        </div>
      )}
      <div className="px-4 py-3 border-t flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">fixmybrick.com</p>
          <p className="text-sm font-semibold text-foreground">Landing page preview</p>
        </div>
        <Button size="sm">Learn More</Button>
      </div>
    </div>
  )
}

function LinkedInPreview({
  accountName = 'Fix My Brick',
  accountAvatar,
  text,
  primaryImage,
  deviceMode
}: PlatformPreviewProps) {
  return (
    <div className="rounded-xl border bg-background shadow-sm">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar className="h-12 w-12">
          {accountAvatar ? <AvatarImage src={accountAvatar} alt={accountName} /> : <AvatarFallback>{initialsFor(accountName)}</AvatarFallback>}
        </Avatar>
        <div>
          <p className="text-sm font-semibold">{accountName}</p>
          <p className="text-xs text-muted-foreground">Company · {deviceMode === 'mobile' ? 'Now' : '1 min ago'}</p>
        </div>
      </div>
      <div className="px-4 pb-4 text-sm whitespace-pre-line text-foreground">
        {text || 'Draft your LinkedIn copy to see a preview here.'}
      </div>
      {primaryImage && (
        <div className="px-4 pb-4">
          <div className="overflow-hidden rounded-lg border">
            <img src={primaryImage} alt="LinkedIn preview" className="h-60 w-full object-cover" />
          </div>
        </div>
      )}
      <div className="px-4 pb-5">
        <div className="rounded-lg border px-4 py-3 bg-muted/40">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">fixmybrick.com</p>
          <p className="text-sm font-semibold">Spotlight: Recent masonry project</p>
          <p className="text-xs text-muted-foreground mt-1">
            Provide a descriptive headline or summary when sharing links on LinkedIn.
          </p>
        </div>
      </div>
      <div className="flex border-t text-sm">
        {['Like', 'Comment', 'Share'].map(action => (
          <button key={action} className="flex-1 py-3 text-muted-foreground hover:text-foreground transition text-center text-xs uppercase tracking-wide">
            {action}
          </button>
        ))}
      </div>
    </div>
  )
}

function TwitterPreview({
  accountName = 'Fix My Brick',
  accountAvatar,
  text,
  hashtags,
  primaryImage,
  deviceMode
}: PlatformPreviewProps) {
  const trimmed = text.trim().slice(0, MAX_TWEET_LENGTH)
  const remaining = MAX_TWEET_LENGTH - trimmed.length

  return (
    <div className="rounded-3xl border bg-background shadow-sm">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar className="h-10 w-10">
          {accountAvatar ? <AvatarImage src={accountAvatar} alt={accountName} /> : <AvatarFallback>{initialsFor(accountName)}</AvatarFallback>}
        </Avatar>
        <div>
          <p className="text-sm font-semibold">{accountName}</p>
          <p className="text-xs text-muted-foreground">@{slugify(accountName)} · {deviceMode === 'mobile' ? 'now' : '1m'}</p>
        </div>
      </div>
      <div className="px-4 pb-4 text-sm text-foreground whitespace-pre-line">
        {trimmed || 'Tweets preview here. Keep copy concise and add hashtags for reach.'}
        {hashtags.length > 0 && (
          <p className="mt-2 text-primary/80">{hashtags.join(' ')}</p>
        )}
      </div>
      {primaryImage && (
        <div className="px-4 pb-4">
          <div className="overflow-hidden rounded-2xl border">
            <img src={primaryImage} alt="Twitter media" className="w-full object-cover max-h-[360px]" />
          </div>
        </div>
      )}
      <div className="px-4 pb-3 text-right text-[11px] text-muted-foreground">
        {remaining} characters left
      </div>
      <div className="flex border-t text-muted-foreground text-xs">
        {['Reply', 'Retweet', 'Like', 'Share'].map(action => (
          <button key={action} className="flex-1 py-3 hover:text-foreground transition">
            {action}
          </button>
        ))}
      </div>
    </div>
  )
}

function initialsFor(name?: string) {
  if (!name) return 'SP'
  return name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0]?.toUpperCase())
    .join('')
    .slice(0, 2) || 'SP'
}

function slugify(name?: string) {
  if (!name) return 'fixmybrick'
  return name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'fixmybrick'
}
